import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  choiceLabelKey,
  EMPLOYMENT_RULES_ROUTE, EMPTY_HIRE_JOB, EMPTY_HOME_ADDRESS, FLSA_CLASSIFICATIONS, HIRE_LOGIN_CHOICES, HR_HIRE_MATCHES_PATH, HR_HIRE_OPTIONS_PATH, HR_HIRE_PATH, PAY_BASES, PAY_RATE_UNITS, Size, WORK_SCHEDULES,
  employeeEditPath, employeeEditProblems, hireLoginHandsOver, hireProblems, inviteChannelProblem, homeAddressPath, homeAddressProblems, t, withRoleDefaults,
  type EmployeeEditDraft, type EmployeeEditStart, type HireAnswer, type HireLoginChoice, type HomeAddressDraft, type HireDraft, type HireJobDraft, type HireMatch, type HireOptions, type StringKey,
} from '@bms2/shared';
import { Button, DatePicker, RadioGroup, Select, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDocumentToday } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { HandOverWindow } from '../settings/HandOverWindow';
import { FirstHireSteps } from './FirstHireSteps';
import styles from './HrPages.module.css';

const EMPTY_OPTIONS: HireOptions = { jobRoles: [], departments: [], managers: [], levels: [], sites: [] };

/** A titled block of the window, its heading in the section-label style. */
function WindowSection({ name, title, children }: { name: string; title: string; children: ReactNode }) {
  return (
    <section className={styles.section} data-hire-section={name}>
      <h3 className={styles.heading}>{title}</h3>
      <div className={styles.fields}>{children}</div>
    </section>
  );
}

/**
 * 🔒 Decision 6841: a refusal that names Settings › HR › Employment Rules links
 * to it, so the work site's missing rules are one press away. Any other
 * refusal draws as the server wrote it.
 */
export function withRulesLink(refusal: string | null): ReactNode {
  if (!refusal || !refusal.includes(t('hr.site.rulesPage'))) return refusal;
  return (
    <span data-hire-rules-refusal="true">
      {refusal} <Link to={EMPLOYMENT_RULES_ROUTE}>{t('hr.site.openRules')}</Link>
    </span>
  );
}

/**
 * The home address's fields (decision 3182): two street lines, the city, the
 * state, the postal code and the country. 🔒 It is the job's private address,
 * never a Location, so nothing here searches or lists the Locations list.
 */
function HomeAddressFields({ home, onChange }: { home: HomeAddressDraft; onChange: (next: HomeAddressDraft) => void }) {
  const line = (index: number) => home.addressLines[index] ?? '';
  const setLine = (index: number, value: string) => {
    const first = index === 0 ? value : line(0);
    const second = index === 1 ? value : line(1);
    onChange({ ...home, addressLines: second ? [first, second] : [first] });
  };
  return (
    <div className={styles.fields} data-home-address="fields">
      <TextInput size={Size.Large} label={t('hr.home.line1')} value={line(0)} onChange={(value) => setLine(0, value)} name="homeLine1" />
      <TextInput size={Size.Large} label={t('hr.home.line2')} value={line(1)} onChange={(value) => setLine(1, value)} name="homeLine2" />
      <div className={styles.controls}>
        <TextInput size={Size.Medium} label={t('hr.home.locality')} value={home.locality} onChange={(locality) => onChange({ ...home, locality })} name="homeLocality" />
        <TextInput size={Size.Small} label={t('hr.home.administrativeArea')} value={home.administrativeArea} onChange={(administrativeArea) => onChange({ ...home, administrativeArea })} name="homeArea" />
        <TextInput size={Size.Small} label={t('hr.home.postalCode')} value={home.postalCode} onChange={(postalCode) => onChange({ ...home, postalCode })} name="homePostalCode" />
        <TextInput size={Size.Small} label={t('hr.home.countryCode')} value={home.countryCode} onChange={(countryCode) => onChange({ ...home, countryCode })} name="homeCountry" />
      </div>
    </div>
  );
}

/**
 * The Job section, which the Hire window and Edit Employee both draw (decision
 * 3181). The job role fills the basis and the overtime class, and the hirer
 * changes either before the save.
 */
function JobFields<T extends HireJobDraft>({ job, options, onChange, exceptManager }: { job: T; options: HireOptions; onChange: (next: T) => void; exceptManager?: string }) {
  const choices = (rows: readonly { id: string; name: string }[]) => rows.map((row) => ({ value: row.id, label: row.name }));
  const named = (prefix: string, values: readonly string[]) => values.map((value) => ({ value, label: t(choiceLabelKey(prefix, value) as StringKey) }));
  return (
    <>
      <TextInput size={Size.Large} label={t('hr.hire.jobTitle')} value={job.title} onChange={(title) => onChange({ ...job, title })} required name="hireTitle" />
      <Select size={Size.Large} label={t('hr.hire.jobRole')} value={job.jobRoleId} placeholder={t('hr.hire.choose')} required options={choices(options.jobRoles)}
        onChange={(id) => onChange(withRoleDefaults(job, options.jobRoles.find((row) => row.id === id)))} dataAttribute={{ 'data-hire-field': 'jobRole' }} />
      <Select size={Size.Large} label={t('hr.hire.department')} value={job.departmentId} placeholder={t('hr.hire.choose')} required options={choices(options.departments)}
        onChange={(departmentId) => onChange({ ...job, departmentId })} dataAttribute={{ 'data-hire-field': 'department' }} />
      <Select size={Size.Large} label={t('hr.hire.manager')} value={job.bossEmploymentId} placeholder={t('hr.hire.managerDerived')}
        options={choices(options.managers.filter((row) => row.id !== exceptManager))} onChange={(bossEmploymentId) => onChange({ ...job, bossEmploymentId })} dataAttribute={{ 'data-hire-field': 'manager' }} />
      <Select size={Size.Medium} label={t('hr.hire.level')} value={job.skillLevelId} placeholder={t('hr.hire.noLevel')} options={choices(options.levels)}
        onChange={(skillLevelId) => onChange({ ...job, skillLevelId })} dataAttribute={{ 'data-hire-field': 'level' }} />
      <div className={styles.controls}>
        <RadioGroup name="hireSchedule" legend={t('hr.hire.schedule')} orientation="horizontal" value={job.workSchedule || null} options={named('hr.schedule', WORK_SCHEDULES)} onChange={(value) => onChange({ ...job, workSchedule: value as T['workSchedule'] })} />
        <RadioGroup name="hireBasis" legend={t('hr.hire.basis')} orientation="horizontal" value={job.basis || null} options={named('hr.basis', PAY_BASES)} onChange={(value) => onChange({ ...job, basis: value as T['basis'] })} />
        <RadioGroup name="hireOvertime" legend={t('hr.hire.overtime')} orientation="horizontal" value={job.flsaClassification || null} options={named('hr.overtime', FLSA_CLASSIFICATIONS)} onChange={(value) => onChange({ ...job, flsaClassification: value as T['flsaClassification'] })} />
      </div>
    </>
  );
}

/** The Person section: a name, then the people on file under it, picked or set aside in so many words (piece 34, *Hiring*). */
function PersonFields({ draft, onChange, apiBaseUrl }: { draft: HireDraft; onChange: (next: HireDraft) => void; apiBaseUrl: string }) {
  const [matches, setMatches] = useState<HireMatch[] | null>(null);
  const [decided, setDecided] = useState<'match' | 'new' | null>(null);
  const ask = async (first: string, last: string) => {
    if (!first.trim() || !last.trim()) return setMatches(null);
    const answer = await post<{ matches: HireMatch[] }>(HR_HIRE_MATCHES_PATH, { firstName: first, lastName: last }, apiBaseUrl);
    setMatches(answer.ok ? answer.data.matches : []);
  };
  if (decided === 'match') {
    const chosen = matches?.find((row) => row.personId === draft.personId);
    return (
      <p className={styles.meta} data-hire-person="match">
        {t('hr.hire.matchChosen')} <strong>{chosen?.name ?? `${draft.firstName} ${draft.lastName}`}</strong>{' '}
        <Button size={Size.Small} text={[t('hr.hire.changePerson')]} actions={[() => { setDecided(null); onChange({ ...draft, personId: '' }); }]} />
      </p>
    );
  }
  return (
    <>
      <div className={styles.controls}>
        <TextInput size={Size.Medium} label={t('hr.hire.firstName')} value={draft.firstName} required name="hireFirstName"
          onChange={(firstName) => { setDecided(null); onChange({ ...draft, firstName, personId: '' }); }} onBlur={() => void ask(draft.firstName, draft.lastName)} />
        <TextInput size={Size.Medium} label={t('hr.hire.lastName')} value={draft.lastName} required name="hireLastName"
          onChange={(lastName) => { setDecided(null); onChange({ ...draft, lastName, personId: '' }); }} onBlur={() => void ask(draft.firstName, draft.lastName)} />
      </div>
      {matches !== null && decided === null ? (
        <div className={styles.fields} data-hire-matches={String(matches.length)}>
          {matches.length === 0 ? <p className={styles.meta}>{t('hr.hire.noMatches')}</p> : <p className={styles.meta}>{t('hr.hire.matchesLead')}</p>}
          {matches.map((row) => (
            <div key={row.personId} className={styles.controls} data-hire-match={row.personId}>
              <span>{row.detail ? `${row.name} · ${row.detail}` : row.name}</span>
              <Button size={Size.Small} text={[t('hr.hire.usePerson')]} actions={[() => { setDecided('match'); onChange({ ...draft, personId: row.personId, matchesShown: matches.length }); }]} />
            </div>
          ))}
          {matches.length > 0 ? (
            <Button size={Size.Small} text={[`${t('hr.hire.newPerson')} (${matches.length})`]} actions={[() => { setDecided('new'); onChange({ ...draft, personId: '', matchesShown: matches.length }); }]} />
          ) : null}
        </div>
      ) : null}
      {decided === 'new' ? <p className={styles.meta} data-hire-person="new">{[t('hr.hire.newPersonChosen'), String(draft.matchesShown)].join(' ')}</p> : null}
    </>
  );
}

/**
 * The Hire window (piece 34, *The Hire window*; decision 3181), drawn through
 * the Form frame: Person, Job, Place, Pay and Access, written in one
 * transaction. 🔒 It opens on the line that the payroll service is entered
 * first and emails the new hire for the tax forms.
 */
export function HireWindow({ apiBaseUrl = '', onClose, onHired }: { apiBaseUrl?: string; onClose: () => void; onHired: (answer: HireAnswer) => void }) {
  const today = useDocumentToday();
  const options = useApi<HireOptions>(HR_HIRE_OPTIONS_PATH, apiBaseUrl);
  const [draft, setDraft] = useState<HireDraft>({
    ...EMPTY_HIRE_JOB, personId: '', firstName: '', lastName: '', matchesShown: 0, startDate: today, workLocationSiteId: '', workEmail: '',
    payRate: '', payRateUnit: '', hoursPerPayPeriod: '', login: 'WorkEmail', loginDestination: '',
  });
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** 🔒 A hire on a hand-over channel answers its code or password once, and the window shows it before the page moves on (decision 0482; T3007). */
  const [handed, setHanded] = useState<HireAnswer | null>(null);
  const held = options.state.kind === 'ready' ? options.state.data : EMPTY_OPTIONS;
  const change = (next: HireDraft) => {
    setTyped(true);
    setDraft(next);
  };
  /** 🔒 T3007: a choice whose provider is not set up is refused by name; absent facts offer every choice and the hire's own refusal answers. */
  const refusedChoice = (choice: HireLoginChoice): string | null => (choice === 'none' || !held.delivery ? null : inviteChannelProblem(choice, held.delivery));
  const refusedChoices = [...new Set(HIRE_LOGIN_CHOICES.map(refusedChoice).filter((problem): problem is string => problem !== null))];
  // 🔨 The window opens on the work email; where mail is not set up it stands on no login instead, never on a choice it draws disabled.
  const login: HireLoginChoice = refusedChoice(draft.login) !== null ? 'none' : draft.login;
  const sent: HireDraft = { ...draft, login, loginDestination: login === draft.login ? draft.loginDestination : '' };
  const problems = hireProblems(sent);
  const hire = async () => {
    const answer = await post<HireAnswer>(HR_HIRE_PATH, sent, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    if (answer.data.handOver) return setHanded(answer.data);
    onHired(answer.data);
  };
  if (handed?.handOver) return <HandOverWindow secret={handed.handOver} onClose={() => onHired(handed)} />;
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.hire.title')}
      width="wide"
      height="tall"
      dataForm="hire"
      typed={typed}
      onCancel={onClose}
      refusal={withRulesLink(refusal ?? (options.state.kind === 'failed' ? options.state.error : null))}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.hire.act'), onAct: () => hire(), disabled: problems.length > 0, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-hire-window="true">
        <p className={styles.meta} data-hire-payroll="true">{t('hr.hire.payrollFirst')}</p>
        {/* 🔒 Decision 6842: the First hire guide stands above the sections while any step is undone. */}
        <FirstHireSteps apiBaseUrl={apiBaseUrl} />
        <WindowSection name="person" title={t('hr.hire.section.person')}>
          <PersonFields draft={draft} onChange={change} apiBaseUrl={apiBaseUrl} />
        </WindowSection>
        <WindowSection name="job" title={t('hr.hire.section.job')}>
          <DatePicker size={Size.Medium} label={t('hr.hire.startDate')} value={draft.startDate} onChange={(startDate) => change({ ...draft, startDate })} required name="hireStartDate" />
          <JobFields job={draft} options={held} onChange={change} />
        </WindowSection>
        <WindowSection name="place" title={t('hr.hire.section.place')}>
          <Select size={Size.Large} label={t('hr.hire.workSite')} value={draft.workLocationSiteId} placeholder={t('hr.hire.choose')} required
            options={held.sites.map((row) => ({ value: row.id, label: row.name }))} onChange={(workLocationSiteId) => change({ ...draft, workLocationSiteId })} dataAttribute={{ 'data-hire-field': 'site' }} />
          <TextInput size={Size.Large} type="email" label={t('hr.hire.workEmail')} value={draft.workEmail} onChange={(workEmail) => change({ ...draft, workEmail })} required name="hireWorkEmail" />
          <p className={styles.meta}>{t('hr.home.hint')}</p>
          <HomeAddressFields home={draft.home ?? EMPTY_HOME_ADDRESS} onChange={(home) => change({ ...draft, home })} />
        </WindowSection>
        <WindowSection name="pay" title={t('hr.hire.section.pay')}>
          <div className={styles.controls}>
            <TextInput size={Size.Small} label={t('hr.hire.payRate')} value={draft.payRate} onChange={(payRate) => change({ ...draft, payRate })} required name="hirePayRate" />
            <RadioGroup name="hirePayUnit" legend={t('hr.hire.payRateUnit')} orientation="horizontal" value={draft.payRateUnit || null}
              options={PAY_RATE_UNITS.map((value) => ({ value, label: t(choiceLabelKey('hr.payUnit', value) as StringKey) }))} onChange={(value) => change({ ...draft, payRateUnit: value as HireDraft['payRateUnit'] })} />
            <TextInput size={Size.Small} label={t('hr.hire.hoursPerPeriod')} value={draft.hoursPerPayPeriod} onChange={(hoursPerPayPeriod) => change({ ...draft, hoursPerPayPeriod })} name="hireHours" />
          </div>
        </WindowSection>
        <WindowSection name="access" title={t('hr.hire.section.access')}>
          <RadioGroup name="hireLogin" legend={t('hr.hire.login')} value={login}
            options={HIRE_LOGIN_CHOICES.map((value) => ({ value, label: t(choiceLabelKey('hr.hire.login', value) as StringKey), disabled: refusedChoice(value) !== null }))} onChange={(value) => change({ ...draft, login: value as HireDraft['login'], loginDestination: '' })} />
          {refusedChoices.map((problem) => <p key={problem} className={styles.meta} data-hire-channel-refused="true">{problem}</p>)}
          {login === 'PersonalEmail' ? (
            <TextInput size={Size.Large} type="email" label={t('hr.hire.loginDestination')} value={draft.loginDestination} onChange={(loginDestination) => change({ ...draft, loginDestination })} required name="hireLoginDestination" />
          ) : null}
          {login === 'Mobile' ? (
            <TextInput size={Size.Large} type="tel" label={t('hr.hire.loginMobile')} value={draft.loginDestination} onChange={(loginDestination) => change({ ...draft, loginDestination })} required name="hireLoginMobile" />
          ) : null}
          {hireLoginHandsOver(login) ? <p className={styles.meta} data-hire-hand-over="true">{t('hr.hire.loginHandOver')}</p> : null}
          {login !== 'none' ? <p className={styles.meta}>{t('hr.hire.loginRole')}</p> : null}
        </WindowSection>
      </div>
    </FormFrame>
  );
}

/**
 * Edit Employee (decisions 3181, 3782): the Hire window's Job section, the work
 * site and the day the change takes effect, written as a dated position, never
 * an overwrite.
 */
export function EditEmployeeWindow({ apiBaseUrl = '', employmentId, start, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  start: EmployeeEditStart;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = useDocumentToday();
  const options = useApi<HireOptions>(HR_HIRE_OPTIONS_PATH, apiBaseUrl);
  const [draft, setDraft] = useState<EmployeeEditDraft>({ ...start, effectiveOn: today });
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const held = options.state.kind === 'ready' ? options.state.data : EMPTY_OPTIONS;
  const change = (next: EmployeeEditDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const problems = employeeEditProblems(draft, today);
  const save = async () => {
    const answer = await post<{ positionId: string }>(employeeEditPath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.edit.title')}
      width="wide"
      dataForm="edit-employee"
      typed={typed}
      onCancel={onClose}
      refusal={withRulesLink(refusal)}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.edit.act'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-edit-employee={employmentId}>
        <p className={styles.meta}>{t('hr.edit.lead')}</p>
        <WindowSection name="job" title={t('hr.hire.section.job')}>
          <DatePicker size={Size.Medium} label={t('hr.edit.effectiveOn')} value={draft.effectiveOn} max={today} onChange={(effectiveOn) => change({ ...draft, effectiveOn })} required name="editEffectiveOn" />
          <JobFields job={draft} options={held} onChange={change} exceptManager={employmentId} />
        </WindowSection>
        {/* 🔒 The work site is dated on the position, so a new site opens a new position from the day above (decision 3782). */}
        <WindowSection name="place" title={t('hr.hire.section.place')}>
          <Select size={Size.Large} label={t('hr.hire.workSite')} value={draft.workLocationSiteId} placeholder={t('hr.hire.choose')} required
            options={held.sites.map((row) => ({ value: row.id, label: row.name }))} onChange={(workLocationSiteId) => change({ ...draft, workLocationSiteId })} dataAttribute={{ 'data-edit-field': 'site' }} />
        </WindowSection>
      </div>
    </FormFrame>
  );
}

/**
 * Edit Home Address (decision 3182): HR's edit of one job's private home
 * address, opened from the Employee page. 🔨 The save names the version it
 * read, so a second editor's change is refused rather than written over.
 */
export function EditHomeAddressWindow({ apiBaseUrl = '', employmentId, start, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  start: HomeAddressDraft;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<HomeAddressDraft>(start);
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: HomeAddressDraft) => {
    setTyped(true);
    setDraft(next);
  };
  const problems = homeAddressProblems(draft);
  const save = async () => {
    const answer = await post<{ version: number }>(homeAddressPath(employmentId), draft, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.home.title')}
      width="wide"
      dataForm="home-address"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.home.act'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-edit-home-address={employmentId}>
        <p className={styles.meta}>{t('hr.home.lead')}</p>
        <HomeAddressFields home={draft} onChange={change} />
      </div>
    </FormFrame>
  );
}
